import mapPieChart from './mapPieChart';

const mapController = (map, options = {}) => {
    if (!map) return;

    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    const disableInteractions = options.disableInteractions && !isTouchDevice;

    const mapContainer = document.querySelector('.elt-map-section-map');
    const mapWrapper = map.closest('.elt-map-section-map-wrapper');
    const zoomRangeInput = mapContainer.querySelector('#mapZoomRange');
    const mapZoomControl = mapContainer.querySelector('.tr-map-zoom-control');
    const navControls = mapContainer.querySelector('.tr-map-nav-buttons');
    const mapItems = document.querySelectorAll('.map-item');

    let scale = zoomRangeInput.value / 100;
    let translateX = 0;
    let translateY = 0;

    let isDragging = false;
    let startX = 0;
    let startY = 0;

    let pinchStartDistance = 0;
    let initialScale = scale;

    // ---- Responsive transform map ----
    const transformMap = {
        jp: {
            default: 'translate(-15%, -100%)',
            375: 'translate(0%, -15%)',
            768: 'translate(-10%, -50%)',
        },
        us: {
            default: 'translate(-5%, 70%)',
            320: 'translate(-25%, -12%)',
            390: 'translate(-25%, 5%)',
            768: 'translate(-25%, 25%)',
            1024: 'translate(-25%, 25%)',
        },
        mx: {
            default: 'translate(-50%, -50%)',
            320: 'translate(-50%, -60%)',
        },
        'region-central-south-america': {
            default: 'translate(-50%, -50%)',
        },
        'region-north-america' : {
            default: 'translate(-50%, -50%)',
            350: 'translate(-54%, -64%)',
        },
        'region-asia-oceania-middle-east' : {
            default: 'translate(-50%, -50%)',
            425: 'translate(-55%, -142%)',
            375: 'translate(-55%, -136%)',
            350: 'translate(-48%, -114%)',
        },
        'region-africa' : {
            default: 'translate(-50%, -50%)',
            375: 'translate(-32%, -49%)',
            350: 'translate(-28%, -32%)',
        },
        europe: {
            default: 'translate(-10%, -40%)',
            480: 'translate(0, -15%)',
        },
    };

    const getResponsiveTransform = (code) => {
        const transforms = transformMap[code];
        if (!transforms) return 'translate(-50%, -50%)';

        const width = window.innerWidth;
        const breakpoints = Object.keys(transforms)
            .filter(k => k !== 'default')
            .map(k => Number(k))
            .sort((a, b) => a - b);

        let selected = 'default';
        for (let bp of breakpoints) {
            if (width <= bp) {
                selected = bp;
                break;
            }
        }

        return transforms[selected] || transforms.default;
    };

    const applyTransform = () => {
        const mapGroup = map.querySelector('#mapGroup') || map;
        if (!mapGroup) return;

        // --- Determine the card ID ---
        const mapId = map.querySelector('g[id]')?.id || map.querySelector('path[id]')?.id;

        let offsetX = 0;
        let offsetY = 0;

        if (mapId && transformMap[mapId]) {
            const transformOffset = getResponsiveTransform(mapId);
            const match = transformOffset.match(/translate\(([^,]+),\s*([^)]+)\)/);
            if (match) {
                const svgRect = map.getBoundingClientRect();

                // Function for converting percentages to pixels
                const parseOffset = (val, total) => {
                    if (typeof val === 'string' && val.includes('%')) {
                        return (parseFloat(val) / 100) * total;
                    }
                    return parseFloat(val) || 0;
                };

                offsetX = parseOffset(match[1], svgRect.width);
                offsetY = parseOffset(match[2], svgRect.height);
            }
        }

        // Apply the resulting offset along with drag/zoom
        mapGroup.setAttribute(
            'transform',
            `translate(${translateX + offsetX}, ${translateY + offsetY}) scale(${scale})`
        );

        updatePositions();
    };

    const updatePositions = () => {
        if (!map || !mapWrapper) return;

        const wrapperRect = mapWrapper.getBoundingClientRect();

        mapItems.forEach(item => {
            const countryCode = item.dataset.code;
            const countryPath = map.querySelector(`#${countryCode}`);

            let left = 0;
            let top = 0;

            if (countryPath) {
                const bbox = countryPath.getBBox();
                const matrix = countryPath.getScreenCTM();
                const point = map.createSVGPoint();
                point.x = bbox.x + bbox.width / 2;
                point.y = bbox.y + bbox.height / 2;
                const transformedPoint = point.matrixTransform(matrix);

                left = transformedPoint.x - wrapperRect.left;
                top = transformedPoint.y - wrapperRect.top;
            } else {
                left = 60;
                top = 60;
                item.classList.add('is-no-map-area');
            }

            const transformValue = getResponsiveTransform(item.dataset.code);

            Object.assign(item.style, {
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                transform: transformValue,
            });

            if (!mapWrapper.contains(item)) {
                mapWrapper.appendChild(item);
            }

            if (!item.classList.contains('is-no-data')) {
                mapPieChart(item);
            }
        });
    };
    const getTouchDistance = (touch1, touch2) => {
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    mapZoomControl.addEventListener('click', ({ target }) => {
        const btn = target.closest('button');
        if (!btn) return;

        let newZoom = Number(zoomRangeInput.value);

        if (btn.id === 'mapZoomIn') {
            newZoom = Math.min(newZoom + 10, Number(zoomRangeInput.max));
        } else if (btn.id === 'mapZoomOut') {
            newZoom = Math.max(newZoom - 10, Number(zoomRangeInput.min));
        } else return;

        zoomRangeInput.value = newZoom;
        zoomRangeInput.dispatchEvent(new Event('input'));
    });

    zoomRangeInput.addEventListener('input', () => {
        scale = zoomRangeInput.value / 100;
        applyTransform();
    });

    navControls.addEventListener('click', ({ target }) => {
        const btn = target.closest('button');
        if (!btn || !btn.dataset.dir) return;

        const step = 50;

        switch (btn.dataset.dir) {
            case 'up': translateY += step; break;
            case 'down': translateY -= step; break;
            case 'left': translateX += step; break;
            case 'right': translateX -= step; break;
        }

        applyTransform();
    });

    map.addEventListener('wheel', (e) => {
        if (disableInteractions) return;
        e.preventDefault();
        const delta = -e.deltaY || e.wheelDelta || -e.detail;
        const direction = delta > 0 ? 1 : -1;

        let newZoom = Number(zoomRangeInput.value) + direction * 10;
        newZoom = Math.max(Number(zoomRangeInput.min), Math.min(newZoom, Number(zoomRangeInput.max)));

        zoomRangeInput.value = newZoom;
        zoomRangeInput.dispatchEvent(new Event('input'));
    }, { passive: false });

    map.addEventListener('mousedown', (e) => {
        if (disableInteractions) return;
        isDragging = true;
        startX = e.clientX - translateX;
        startY = e.clientY - translateY;
        map.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', (e) => {
        if (disableInteractions || !isDragging) return;
        translateX = e.clientX - startX;
        translateY = e.clientY - startY;
        applyTransform();
    });

    document.addEventListener('mouseup', () => {
        if (disableInteractions || !isDragging) return;
        isDragging = false;
        map.style.cursor = 'default';
    });

    map.addEventListener('mouseleave', () => {
        if (disableInteractions) return;
        isDragging = false;
        map.style.cursor = 'grab';
    });

    map.addEventListener('touchstart', (e) => {
        if (disableInteractions) return;

        if (e.touches.length === 1) {
            isDragging = true;
            startX = e.touches[0].clientX - translateX;
            startY = e.touches[0].clientY - translateY;
        } else if (e.touches.length === 2) {
            isDragging = false;
            pinchStartDistance = getTouchDistance(e.touches[0], e.touches[1]);
            initialScale = scale;
        }

        e.preventDefault();
    }, { passive: false });

    map.addEventListener('touchmove', (e) => {
        if (disableInteractions) return;

        if (e.touches.length === 1 && isDragging) {
            translateX = e.touches[0].clientX - startX;
            translateY = e.touches[0].clientY - startY;
            applyTransform();
        } else if (e.touches.length === 2) {
            const newDistance = getTouchDistance(e.touches[0], e.touches[1]);
            const zoomFactor = newDistance / pinchStartDistance;
            scale = Math.max(zoomRangeInput.min / 100, Math.min(zoomRangeInput.max / 100, initialScale * zoomFactor));
            zoomRangeInput.value = Math.round(scale * 100);
            applyTransform();
        }

        e.preventDefault();
    }, { passive: false });

    map.addEventListener('touchend', (e) => {
        if (disableInteractions) return;
        if (e.touches.length < 2) isDragging = false;
    }, { passive: false });

    document.addEventListener('click', (e) => {
        const link = e.target.closest('.map-item-link.no-details-data');
        if (!link) return;

        e.preventDefault();

        const item = link.closest('.map-item');
        const noDataBlock = item.querySelector('.is-no-data');

        if (!item || !noDataBlock) return;

        item.innerHTML = noDataBlock.innerHTML;
        item.classList.add('is-no-data');
    });

// ---- Hover delegation: .map-item <=> corresponding <path|g> highlight ----
    const addActive = (el) => el?.classList?.add('active');
    const removeActive = (el) => el?.classList?.remove('active');

    document.addEventListener('mouseover', (e) => {
        let target = e.target;

        // Hover over .map-item
        const item = target.closest('.map-item');
        if (item) {
            const code = item.dataset.code;
            if (!code) return;

            const mapEl = map.querySelector(`#${code}`);
            addActive(item);
            addActive(mapEl);
            return;
        }

        // Point to <path> or <g> inside the map
        const mapEl = target.closest('path[id], g[id]');
        if (mapEl && map.contains(mapEl)) {
            const code = mapEl.id;
            const item = document.querySelector(`.map-item[data-code="${code}"]`);
            addActive(mapEl);
            addActive(item);
            return;
        }
    });

    document.addEventListener('mouseout', (e) => {
        let target = e.target;

        // .map-item
        const item = target.closest('.map-item');
        if (item) {
            const code = item.dataset.code;
            if (!code) return;

            const mapEl = map.querySelector(`#${code}`);
            removeActive(item);
            removeActive(mapEl);
            return;
        }

        // path or g
        const mapEl = target.closest('path[id], g[id]');
        if (mapEl && map.contains(mapEl)) {
            const code = mapEl.id;
            const item = document.querySelector(`.map-item[data-code="${code}"]`);
            removeActive(mapEl);
            removeActive(item);
            return;
        }
    });

    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
        const now = new Date().getTime();
        if (now - lastTouchEnd <= 300) {
            e.preventDefault();
        }
        lastTouchEnd = now;
    }, { passive: false });

    document.addEventListener('gesturestart', (e) => {
        e.preventDefault();
    }, { passive: false });

    window.addEventListener('resize', updatePositions);

    if (mapWrapper && 'ResizeObserver' in window) {
        const observer = new ResizeObserver(updatePositions);
        observer.observe(mapWrapper);
    }

    applyTransform();
    updatePositions();

    requestAnimationFrame(() => {
        mapItems.forEach(item => {
            item.style.opacity = '1';
            item.style.visibility = 'visible';
        });
    });

    const setTransform = (newScale, newTranslateX, newTranslateY) => {
        scale = newScale;
        translateX = newTranslateX;
        translateY = newTranslateY;
        zoomRangeInput.value = Math.round(scale * 100);
        applyTransform();
    };

    return {
        updatePositions,
        setTransform,
        getTransform: () => ({ scale, translateX, translateY }),
    };
};

export default mapController;
